import {
  ChangeDetectionStrategy,
  Component,
  inject,
  effect,
  ElementRef,
  viewChild,
  PLATFORM_ID,
  computed,
  ChangeDetectorRef,
  signal,
} from "@angular/core";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { RouterOutlet, RouterLink, Router, NavigationEnd } from "@angular/router";
import { animate } from "motion";
import { filter } from "rxjs/operators";
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { AuthService } from "./auth.service";
import { ThemeService } from "./theme";
import { NotificationService } from "./notification";
import { AccountStatus } from "./models/auth.model";
import { PwaService } from "./pwa.service";
import { InternetService } from "./services/internet.service";
import { APP_VERSION } from "./version";
import { PwaInstallPrompt } from "./pwa-install-prompt";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-root",
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    MatIconModule,
    PwaInstallPrompt,
  ],
  template: `
    <!-- Liquid Background & Grid -->
    <div class="liquid-bg">
      @let colors = activeColors();
      <div
        class="liquid-blob top-[-20%] left-[-10%] w-[800px] h-[800px] animate-liquid"
        [class]="colors[0]"
      ></div>
      <div
        class="liquid-blob bottom-[-20%] right-[-10%] w-[900px] h-[900px] animate-liquid"
        [class]="colors[1]"
        style="animation-delay: -2s"
      ></div>
      <div
        class="liquid-blob top-[30%] right-[-20%] w-[700px] h-[700px] animate-liquid"
        [class]="colors[2]"
        style="animation-delay: -4s"
      ></div>
      <div
        class="liquid-blob bottom-[10%] left-[10%] w-[600px] h-[600px] animate-liquid"
        [class]="colors[3]"
        style="animation-delay: -6s"
      ></div>
    </div>

    <!-- Main App Content -->
    <div class="min-h-screen transition-colors duration-100">
      @if (authService.loading()) {
        <div
          class="fixed inset-0 flex items-center justify-center z-[10000] bg-transparent transition-colors duration-500"
        >
          <img
            src="kicgle.svg"
            alt="Kicgle"
            class="w-full max-w-[140px] md:max-w-[200px] h-auto filter dark:brightness-110 object-contain drop-shadow-md"
            referrerpolicy="no-referrer"
          />
        </div>
      } @else if (authService.isSigningUp()) {
        <div
          class="fixed inset-0 flex items-center justify-center z-[10000] bg-black/60 dark:bg-black/80 backdrop-blur-xl transition-colors duration-500 p-3 sm:p-6 overflow-y-auto"
        >
          <div
            class="w-full max-w-4xl glass-card !rounded-[32px] sm:!rounded-[44px] p-6 sm:p-8 md:p-10 animate-ios-slide relative overflow-hidden border border-white/15 dark:border-white/10 shadow-[0_0_60px_rgba(0,122,255,0.15)] my-auto"
          >
            <!-- Grid background for card -->
            <div
              class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+CjxwYXRoIGQ9Ik0wIDBoMjB2MjBIMHoiIGZpbGw9Im5vbmUiLz4KPHBhdGggZD0iTTAgMTBoMjBNMTAgMHYyMCIgc3Ryb2tlPSJyZ2JhKDEyOCwgMTI4LCAxMjgsIDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz4KPC9zdmc+')] opacity-50 pointer-events-none"
            ></div>

            <div class="relative z-10 space-y-6 sm:space-y-8 py-2">
              <div class="flex flex-col items-center text-center">
                <div class="relative w-16 h-16 sm:w-20 sm:h-20 mb-4 sm:mb-5">
                  <div
                    class="absolute inset-0 bg-ios-blue/20 blur-xl rounded-full animate-pulse"
                  ></div>
                  <div
                    class="absolute inset-0 border-2 border-ios-blue/30 rounded-full animate-[spin_4s_linear_infinite]"
                  ></div>
                  <div
                    class="absolute inset-2 border border-dashed border-ios-blue/40 rounded-full animate-[spin_3s_linear_infinite_reverse]"
                  ></div>
                  <div
                    class="w-full h-full bg-ios-blue/10 backdrop-blur-md rounded-full flex items-center justify-center relative z-10 border border-white/10"
                  >
                    <mat-icon class="text-ios-blue text-2xl sm:text-3xl"
                      >verified_user</mat-icon
                    >
                  </div>
                </div>
                <h2 class="text-2xl sm:text-3xl font-black text-ios-title tracking-tight">
                  System Setup
                </h2>
                <p class="text-xs sm:text-sm text-slate-500 dark:text-zinc-400 font-semibold mt-1">
                  Automated Security, Verification & Backend Provisioning
                </p>

                <div class="w-full max-w-xl mt-5 relative">
                  <div
                    class="absolute -inset-2 bg-ios-blue/20 blur-lg rounded-full opacity-50"
                  ></div>
                  <div
                    class="relative bg-black/10 dark:bg-white/5 rounded-full h-2 overflow-hidden border border-white/5"
                  >
                    <div
                      class="bg-gradient-to-r from-ios-blue to-ios-purple h-full rounded-full transition-all duration-700 ease-out relative"
                      [style.width.%]="authService.completionPercentage()"
                    >
                      <div
                        class="absolute top-0 right-0 bottom-0 w-10 bg-gradient-to-r from-transparent to-white/50 blur-[2px]"
                      ></div>
                    </div>
                  </div>
                  <div class="flex items-center justify-between w-full mt-2.5 px-1">
                    <p class="ios-sub-header text-ios-blue/60 text-[11px] font-bold">Verification Progress</p>
                    <p class="text-[12px] font-black text-ios-blue">
                      {{ authService.completionPercentage() | number: "1.0-0" }}%
                    </p>
                  </div>
                </div>
              </div>

              <!-- Strict 4-Column CSS Grid across all viewport breakpoints -->
              <div class="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4 w-full">
                @for (
                  item of authService.signupChecklist();
                  track item.id;
                  let i = $index
                ) {
                  <div
                    class="group relative flex flex-col items-center justify-between p-2.5 sm:p-4 rounded-2xl sm:rounded-3xl border transition-all duration-500 overflow-hidden text-center min-w-0 min-h-[110px] sm:min-h-[135px]"
                    [class.bg-ios-blue/10]="item.status === 'loading'"
                    [class.!border-ios-blue/60]="item.status === 'loading'"
                    [class.shadow-[0_0_20px_rgba(0,122,255,0.25)]]="item.status === 'loading'"
                    [class.scale-[1.02]]="item.status === 'loading'"
                    [class.bg-emerald-500/10]="item.status === 'success'"
                    [class.!border-emerald-500/40]="item.status === 'success'"
                    [class.bg-red-500/10]="item.status === 'error'"
                    [class.!border-red-500/40]="item.status === 'error'"
                    [class.bg-white/5]="item.status === 'pending'"
                    [class.border-white/10]="item.status === 'pending'"
                    [class.opacity-45]="item.status === 'pending'"
                  >
                    @if (item.status === 'loading') {
                      <div class="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-ios-blue via-ios-purple to-ios-blue animate-pulse"></div>
                    } @else if (item.status === 'success') {
                      <div class="absolute inset-x-0 top-0 h-1 bg-emerald-500"></div>
                    } @else if (item.status === 'error') {
                      <div class="absolute inset-x-0 top-0 h-1 bg-red-500"></div>
                    }

                    <div class="relative flex items-center justify-center w-7 h-7 sm:w-9 sm:h-9 rounded-full mb-1 shrink-0">
                      @if (item.status === 'loading') {
                        <div class="absolute inset-0 border-2 border-ios-blue/30 rounded-full"></div>
                        <div class="absolute inset-0 border-2 border-ios-blue border-t-transparent rounded-full animate-spin"></div>
                        <mat-icon class="text-ios-blue text-[15px] sm:text-[18px]">sync</mat-icon>
                      } @else if (item.status === 'success') {
                        <div class="w-full h-full bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center border border-emerald-500/40 shadow-[0_0_12px_rgba(16,185,129,0.2)]">
                          <mat-icon class="text-[15px] sm:text-[18px] font-black">check</mat-icon>
                        </div>
                      } @else if (item.status === 'error') {
                        <div class="w-full h-full bg-red-500/20 text-red-500 rounded-full flex items-center justify-center border border-red-500/40">
                          <mat-icon class="text-[15px] sm:text-[18px] font-black">close</mat-icon>
                        </div>
                      } @else {
                        <div class="w-full h-full bg-white/5 text-slate-400 dark:text-zinc-500 rounded-full flex items-center justify-center border border-white/10">
                          <span class="text-[10px] sm:text-xs font-mono font-bold">{{ i + 1 }}</span>
                        </div>
                      }
                    </div>

                    <p class="text-[10px] sm:text-xs md:text-sm font-black tracking-tight leading-tight line-clamp-2 px-0.5 text-slate-900 dark:text-white my-auto w-full">
                      {{ item.label }}
                    </p>

                    <div class="mt-1 w-full flex flex-col items-center">
                      @if (item.error) {
                        <span class="text-[8px] sm:text-[9px] font-mono text-red-400 font-bold bg-red-500/15 px-1 py-0.5 rounded border border-red-500/30 truncate max-w-full">
                          {{ item.error }}
                        </span>
                      } @else if (item.status === 'loading') {
                        <span class="text-[8px] sm:text-[9px] font-black uppercase tracking-wider text-ios-blue">
                          Checking
                        </span>
                      } @else if (item.status === 'success') {
                        <span class="text-[8px] sm:text-[9px] font-black uppercase tracking-wider text-emerald-500">
                          Verified
                        </span>
                      } @else {
                        <span class="text-[8px] sm:text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                          Queued
                        </span>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>

            @if (authService.hasSignupError()) {
              <button
                (click)="authService.isSigningUp.set(false)"
                class="relative z-10 w-full h-[52px] bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[13px] font-black tracking-widest uppercase transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,255,255,0.05)] mt-6 text-slate-900 dark:text-white"
              >
                Go Back & Fix Errors
              </button>
            }
          </div>
        </div>
      } @else {
        @if (
          authService.isAuthenticated() &&
          authService.userProfile()?.status !== AccountStatus.ACTIVE
        ) {
          <div
            class="fixed inset-0 flex items-center justify-center z-[9998] bg-transparent backdrop-blur-md p-6"
          >
            <div
              class="w-full max-w-md glass-card !rounded-[40px] p-8 md:p-12 text-center animate-ios-slide space-y-8 relative overflow-hidden border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.2)]"
            >
              <!-- Grid background -->
              <div
                class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+CjxwYXRoIGQ9Ik0wIDBoMjB2MjBIMHoiIGZpbGw9Im5vbmUiLz4KPHBhdGggZD0iTTAgMTBoMjBNMTAgMHYyMCIgc3Ryb2tlPSJyZ2JhKDEyOCwgMTI4LCAxMjgsIDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz4KPC9zdmc+')] opacity-50 pointer-events-none"
              ></div>

              <div class="relative z-10 flex flex-col items-center gap-6">
                <div
                  class="relative w-28 h-28 flex items-center justify-center group"
                >
                  <div
                    class="absolute inset-0 bg-ios-blue/20 blur-2xl rounded-full scale-150 animate-pulse"
                  ></div>
                  <div
                    class="absolute inset-0 border border-ios-blue/30 rounded-full animate-[spin_8s_linear_infinite]"
                  ></div>
                  <div
                    class="absolute inset-2 border border-dashed border-ios-blue/20 rounded-full animate-[spin_6s_linear_infinite_reverse]"
                  ></div>
                  <div
                    class="w-20 h-20 rounded-full bg-ios-blue/10 backdrop-blur-md flex items-center justify-center border border-white/10 relative z-10 shadow-[0_0_30px_rgba(0,122,255,0.2)]"
                  >
                    <mat-icon class="text-ios-blue text-4xl">
                      @switch (authService.userProfile()?.status) {
                        @case (AccountStatus.PENDING) {
                          hourglass_empty
                        }
                        @case (AccountStatus.SUSPENDED) {
                          block
                        }
                        @case (AccountStatus.DELETED) {
                          delete_forever
                        }
                        @case (AccountStatus.INACTIVE) {
                          power_settings_new
                        }
                        @default {
                          error_outline
                        }
                      }
                    </mat-icon>
                  </div>
                </div>

                <div class="space-y-4">
                  <h2
                    class="text-2xl font-black text-ios-title tracking-tight uppercase"
                  >
                    @switch (authService.userProfile()?.status) {
                      @case (AccountStatus.PENDING) {
                        Verification Pending
                      }
                      @case (AccountStatus.SUSPENDED) {
                        Access Suspended
                      }
                      @case (AccountStatus.DELETED) {
                        Account Terminated
                      }
                      @case (AccountStatus.INACTIVE) {
                        System Inactive
                      }
                      @default {
                        Access Restricted
                      }
                    }
                  </h2>
                  <div class="h-px w-12 bg-ios-blue/30 mx-auto"></div>
                  <p
                    class="text-[14px] font-medium opacity-60 leading-relaxed max-w-[280px] mx-auto"
                  >
                    @switch (authService.userProfile()?.status) {
                      @case (AccountStatus.PENDING) {
                        Your identity is currently being verified by our
                        automated systems. Please allow up to 24 hours.
                      }
                      @case (AccountStatus.SUSPENDED) {
                        Your access has been temporarily revoked due to a
                        security policy violation.
                      }
                      @case (AccountStatus.DELETED) {
                        This identity profile has been permanently purged from
                        the network.
                      }
                      @case (AccountStatus.INACTIVE) {
                        Your profile is currently dormant. Contact system
                        administration to restore access.
                      }
                      @default {
                        You lack the necessary clearance to access this sector.
                      }
                    }
                  </p>
                </div>
              </div>

              <div class="relative z-10 pt-6 space-y-4">
                <button
                  (click)="authService.signOut()"
                  class="w-full h-[54px] bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[14px] font-black tracking-widest uppercase transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,255,255,0.05)]"
                >
                  Disconnect
                </button>
                <a
                  href="mailto:support@kicgle.com"
                  class="block text-ios-blue text-[12px] font-bold tracking-wider uppercase hover:text-ios-blue/80 transition-colors"
                >
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        } @else {
          <div class="route-container" #routeContainer>
            <router-outlet></router-outlet>
          </div>

          <!-- Global Footer -->
          <footer class="w-full mt-16 pb-12 flex flex-col items-center gap-5 animate-ios-fade text-center relative z-10 px-4">
            <!-- Country Indicator (Above Links, no background style) -->
            <div>
              <button
                (click)="toggleTheme()"
                class="inline-flex items-center gap-2 text-[13px] md:text-[14px] font-bold opacity-70 hover:opacity-100 transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer"
                title="Click to toggle light/dark theme"
              >
                <span class="text-base leading-none">{{ countryInfo().flag }}</span>
                <span>{{ countryInfo().name }}</span>
              </button>
            </div>

            <!-- Links (No divider line above) -->
            <div class="flex items-center justify-center gap-6 md:gap-10 text-[13px] md:text-[14px] font-bold tracking-wide opacity-60 hover:opacity-100 transition-all duration-300">
              <a routerLink="/about" class="hover:text-ios-blue transition-colors">About</a>
              <a routerLink="/ever" class="hover:text-ios-blue transition-colors">Ever</a>
              <a routerLink="/policies" class="hover:text-ios-blue transition-colors">Policies</a>
            </div>

            <!-- Copyright Section: Kicgle SVG logo, copyright symbol, and version code -->
            <div class="flex items-center justify-center gap-2.5 text-[11px] md:text-[12px] font-mono tracking-wider opacity-60">
              <img src="kicgle.svg" alt="Kicgle" class="h-4 w-auto object-contain" referrerpolicy="no-referrer" />
              <span>©</span>
              <span class="font-bold">{{ appVersion }}</span>
            </div>
          </footer>
        }
      }

      @if (authService.isAuthenticated()) {}

      <!-- Active Ad Banner -->


      <!-- Global Notifications -->
      <div
        class="fixed bottom-8 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-3 w-full max-w-md px-4 pointer-events-none"
      >
        @for (n of notificationService.notifications(); track n.id) {
          <div
            class="flex items-center gap-4 p-3 pr-4 glass-card !rounded-full animate-ios-slide pointer-events-auto border shadow-2xl backdrop-blur-md transition-all duration-500"
            [class.border-green-500/30]="n.type === 'success'"
            [class.bg-green-500/5]="n.type === 'success'"
            [class.shadow-[0_10px_40px_rgba(34,197,94,0.15)]]="
              n.type === 'success'
            "
            [class.border-red-500/30]="n.type === 'error'"
            [class.bg-red-500/5]="n.type === 'error'"
            [class.shadow-[0_10px_40px_rgba(239,68,68,0.15)]]="
              n.type === 'error'
            "
            [class.border-blue-500/30]="n.type === 'info'"
            [class.bg-blue-500/5]="n.type === 'info'"
            [class.shadow-[0_10px_40px_rgba(59,130,246,0.15)]]="
              n.type === 'info'
            "
            [class.border-yellow-500/30]="n.type === 'warning'"
            [class.bg-yellow-500/5]="n.type === 'warning'"
            [class.shadow-[0_10px_40px_rgba(234,179,8,0.15)]]="
              n.type === 'warning'
            "
          >
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center relative"
            >
              <div
                class="absolute inset-0 rounded-full opacity-20 blur-md"
                [class.bg-green-500]="n.type === 'success'"
                [class.bg-red-500]="n.type === 'error'"
                [class.bg-blue-500]="n.type === 'info'"
                [class.bg-yellow-500]="n.type === 'warning'"
              ></div>
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center border border-white/10 bg-black/20 dark:bg-white/10 backdrop-blur-md relative z-10"
              >
                <mat-icon
                  class="text-[16px] font-bold"
                  [class.text-green-500]="n.type === 'success'"
                  [class.text-red-500]="n.type === 'error'"
                  [class.text-blue-500]="n.type === 'info'"
                  [class.text-yellow-500]="n.type === 'warning'"
                >
                  @switch (n.type) {
                    @case ("success") {
                      check
                    }
                    @case ("error") {
                      close
                    }
                    @case ("info") {
                      info_outline
                    }
                    @case ("warning") {
                      warning_amber
                    }
                  }
                </mat-icon>
              </div>
            </div>
            <span class="text-[13px] font-bold tracking-wide flex-1">{{
              n.message
            }}</span>
            <button
              (click)="notificationService.remove(n.id)"
              class="w-8 h-8 flex items-center justify-center hover:bg-black/10 dark:hover:bg-white/10 rounded-full transition-colors opacity-50 hover:opacity-100"
            >
              <mat-icon class="text-[16px]">close</mat-icon>
            </button>
          </div>
        }
      </div>

      <!-- Offline Blocking Overlay -->
      @if (!internetService.isOnline()) {
        <div class="fixed inset-0 z-[20000] flex items-center justify-center bg-black/90 backdrop-blur-3xl animate-ios-fade p-6 text-center">
            <div class="space-y-8 max-w-sm">
                <div class="w-24 h-24 mx-auto bg-red-500/20 rounded-full flex items-center justify-center border border-red-500/30 animate-pulse">
                    <mat-icon class="!text-5xl text-red-500">wifi_off</mat-icon>
                </div>
                <div class="space-y-3">
                    <h2 class="text-3xl font-black text-white tracking-tight">You are offline</h2>
                    <p class="text-white/60 font-medium leading-relaxed">
                        Access to the Kicgle network is restricted in disconnected mode. Please restore your link to continue.
                    </p>
                </div>
                <div class="pt-4">
                  <div class="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 text-[11px] font-mono text-white/40 uppercase tracking-widest">
                    <div class="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping"></div>
                    Attempting to reconnect...
                  </div>
                </div>
            </div>
        </div>
      }

      <!-- PWA Liquid Glass Install Prompt Component -->
      <app-pwa-install-prompt></app-pwa-install-prompt>

    </div>
  `,
})
export class App {
  authService = inject(AuthService);
  themeService = inject(ThemeService);
  notificationService = inject(NotificationService);
  pwaService = inject(PwaService);
  internetService = inject(InternetService);
  private router = inject(Router);
  private swUpdate = inject(SwUpdate);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  private cdr = inject(ChangeDetectorRef);
  AccountStatus = AccountStatus;

  appVersion = APP_VERSION;
  countryInfo = signal<{ flag: string; name: string }>({ flag: '🇮🇳', name: 'India' });

  toggleTheme() {
    const current = this.themeService.theme();
    if (current === 'dark') {
      this.themeService.setTheme('light');
    } else if (current === 'light') {
      this.themeService.setTheme('dark');
    } else {
      const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
      this.themeService.setTheme(isDark ? 'light' : 'dark');
    }
  }

  private initCountryInfo() {
    if (!this.isBrowser) return;

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (tz.includes('Kolkata') || tz.includes('Calcutta') || tz.includes('Asia/Kolkata')) {
        this.countryInfo.set({ flag: '🇮🇳', name: 'India' });
      } else if (tz.includes('America') || tz.includes('New_York') || tz.includes('Los_Angeles') || tz.includes('Chicago')) {
        this.countryInfo.set({ flag: '🇺🇸', name: 'United States' });
      } else if (tz.includes('London') || tz.includes('Europe/London')) {
        this.countryInfo.set({ flag: '🇬🇧', name: 'United Kingdom' });
      } else if (tz.includes('Tokyo') || tz.includes('Asia/Tokyo')) {
        this.countryInfo.set({ flag: '🇯🇵', name: 'Japan' });
      } else if (tz.includes('Europe/Paris') || tz.includes('Paris')) {
        this.countryInfo.set({ flag: '🇫🇷', name: 'France' });
      } else if (tz.includes('Europe/Berlin') || tz.includes('Berlin')) {
        this.countryInfo.set({ flag: '🇩🇪', name: 'Germany' });
      } else if (tz.includes('Australia') || tz.includes('Sydney')) {
        this.countryInfo.set({ flag: '🇦🇺', name: 'Australia' });
      } else if (tz.includes('Toronto') || tz.includes('Vancouver') || tz.includes('Canada')) {
        this.countryInfo.set({ flag: '🇨🇦', name: 'Canada' });
      }
    } catch {
      // Fallback
    }

    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        if (data && data.country_name) {
          const flag = this.getFlagEmoji(data.country_code || data.country);
          this.countryInfo.set({
            flag: flag || '🇮🇳',
            name: data.country_name
          });
          this.cdr.markForCheck();
        }
      })
      .catch((err) => {
        console.log('Location fetch bypassed:', err);
      });
  }

  private getFlagEmoji(countryCode: string): string {
    if (!countryCode || countryCode.length !== 2) return '🌐';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  }

  routeContainer = viewChild<ElementRef>("routeContainer");

  activeColors = computed(() => {
    const styleId = this.themeService.backgroundStyle();
    const theme = this.themeService.backgroundThemes.find(
      (t) => t.id === styleId,
    );
    return theme ? theme.colors : this.themeService.backgroundThemes[0].colors;
  });

  setupRouteBasedUpdateCheck() {
    if (!this.isBrowser || !this.swUpdate.isEnabled) return;

    // 1. Silent Background Detection: Subscribe to swUpdate.versionUpdates for VersionReadyEvent
    // Automatically activate new version immediately using the skipWaiting strategy
    this.swUpdate.versionUpdates.pipe(
      filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
    ).subscribe(async (evt: VersionReadyEvent) => {
      await this.pwaService.executeSkipWaitingAndActivate(evt.latestVersion.hash);
    });

    // Handle unrecoverable state automatically
    this.swUpdate.unrecoverable.subscribe(async () => {
      console.warn('PWA: Unrecoverable state detected. Purging cache and reloading...');
      await this.clearBrowserCaches();
      window.location.href = window.location.origin + window.location.pathname + '?reload=' + Date.now();
    });

    // Actively check on startup
    this.checkUpdateSilently();

    // Check periodically every 30 seconds for Cloud Run updates
    setInterval(() => {
      this.checkUpdateSilently();
    }, 30000);

    // Trigger update probe on EVERY navigation event
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkUpdateSilently();
    });

    // Re-check update when returning to focus, visibility change, or online
    window.addEventListener('focus', () => {
      this.checkUpdateSilently();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkUpdateSilently();
      }
    });
    window.addEventListener('online', () => {
      this.checkUpdateSilently();
    });
    window.addEventListener('pwa-sync-request', () => {
      this.checkUpdateSilently();
    });
  }

  private async checkUpdateSilently() {
    if (!this.isBrowser || !this.swUpdate.isEnabled) return;
    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      // Network throttled or offline - safely continue
    }
  }

  private async clearBrowserCaches(): Promise<void> {
    if (typeof window !== 'undefined' && 'caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
        console.log('PWA: All structural browser caches purged.');
      } catch (e) {
        console.warn('PWA: Cache clearing error:', e);
      }
    }
  }

  constructor() {
    this.setupRouteBasedUpdateCheck();
    this.initCountryInfo();


    effect(() => {
      if (this.authService.allCompleted()) {
        setTimeout(() => {
          this.authService.isSigningUp.set(false);
        }, 1000);
      }
    });



    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (!this.isBrowser) return;
        const el = this.routeContainer()?.nativeElement;
        if (el) {
          animate(
            el,
            { opacity: [0, 1], y: [10, 0] },
            { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
          );
        }
      });
  }
}
